/**
 * BankAccount — bank connections (Stripe Financial Connections, through the Olene account)
 *
 * - Integrated with Sidebar for consistent app chrome
 * - Connections belong to the person's Olene account, so every Olene app sees the same banks
 * - People sign in to their bank in Stripe's secure window; PayClear never sees bank credentials
 * - One row per connected bank account ("enrollments" below), each with disconnect
 * - Weekly transaction view across all linked accounts
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "../auth/AuthProvider";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";

const API_URL = process.env.REACT_APP_API_URL;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;


function BankAccount() {
    const auth  = useAuth();
    const token = auth.user?.access_token;

    const handleSignOut = () => {
        auth.signOut();
    };

    const authHeaders = useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    }), [token]);

    // ── State ────────────────────────────────────────────────────────────────
    const [enrollments, setEnrollments]   = useState([]);   // [{enrollment_id, institution_name, linked_at, disconnected}]
    const [balances, setBalances]         = useState([]);   // accounts from all enrollments
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading]           = useState(true);
    const connectReady = Boolean(STRIPE_PK);  // the connect button is enabled when Stripe is configured
    const [weekOffset, setWeekOffset]     = useState(0);
    const [selectedAccount, setSelectedAccount] = useState("all");
    const [syncing, setSyncing]           = useState(false);
    const [classifying, setClassifying]   = useState(false);
    const [showCalendarSync, setShowCalendarSync] = useState(false);
    const [calendarStartDate, setCalendarStartDate] = useState("");
    // null = hidden; string = message to show in the full-screen loading overlay
    const [connectingStatus, setConnectingStatus] = useState(null);
    // { transaction_id: { category_name, source } }
    const [userAssignments, setUserAssignments] = useState({});
    // Data disclaimer — shown once per browser session before opening the bank connect window
    const [showDisclaimer, setShowDisclaimer] = useState(false);

    // ── Weekly navigation ────────────────────────────────────────────────────
    const getWeekRange = (offset = 0) => {
        const today = new Date();
        today.setDate(today.getDate() + offset * 7);
        const day   = today.getDay();
        const start = new Date(today);
        start.setDate(today.getDate() - day);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return [start, end];
    };

    const [startDate, endDate] = useMemo(() => getWeekRange(weekOffset), [weekOffset]);

    const minDate   = new Date();
    minDate.setDate(minDate.getDate() - 90);
    minDate.setHours(0, 0, 0, 0);

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    const isCurrentWeek       = endDate >= todayDate && startDate <= todayDate;
    const isEarliestAllowedWeek = startDate <= minDate;

    const formatDate = (d) => d.toLocaleDateString("en-CA");
    const startISO   = formatDate(startDate);
    const endISO     = formatDate(endDate);

    const visibleTransactions = useMemo(() => {
        const byWeek = transactions.filter(t => t.date >= startISO && t.date <= endISO);
        if (selectedAccount === "all") return byWeek;
        return byWeek.filter(t => t.account_id === selectedAccount);
    }, [transactions, startISO, endISO, selectedAccount]);

    // ── Data refresh ─────────────────────────────────────────────────────────
    const refreshAll = useCallback(async () => {
        if (!token) return;
        try {
            const statusRes = await fetch(`${API_URL}/api/bank/status`, { headers: authHeaders });
            if (!statusRes.ok) return;
            const status = await statusRes.json();

            setEnrollments(status.enrollments || []);
            if (!status.linked) return;

            const [balRes, txRes, assignRes] = await Promise.all([
                fetch(`${API_URL}/api/bank/balance`,                    { headers: authHeaders }),
                fetch(`${API_URL}/api/bank/transactions`,               { headers: authHeaders }),
                fetch(`${API_URL}/api/bank/transactions/categories`,    { headers: authHeaders }),
            ]);
            if (balRes.ok) {
                const data = await balRes.json();
                setBalances(data.accounts || []);
            }
            if (txRes.ok) {
                const data = await txRes.json();
                setTransactions(data.transactions || []);
            }
            if (assignRes.ok) {
                setUserAssignments(await assignRes.json());
            }
        } catch (err) {
            console.error("Bank refresh error:", err);
        }
    }, [authHeaders, token]);

    // Initial load + session sync
    useEffect(() => {
        if (!token) return;

        const doLoad = async () => {
            await refreshAll();

            // Sync on every visit: it reads what Stripe already pulled (free), and does nothing
            // when there's nothing new.
            const willSync = true;

            // Set classifying before setLoading so they batch into the same render,
            // preventing the table from flashing empty categories.
            if (willSync) setClassifying(true);
            setLoading(false);

            if (willSync) {
                const lastSession = localStorage.getItem("payclear_last_session");
                let sessionDays = 30;
                if (lastSession) {
                    const daysSince = Math.ceil((Date.now() - new Date(lastSession).getTime()) / 86400000);
                    sessionDays = Math.max(1, Math.min(daysSince + 1, 730));
                }
                localStorage.setItem("payclear_last_session", new Date().toISOString());
                handleSync(sessionDays);
            }
        };
        doLoad();
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Background AI classification ──────────────────────────────────────────
    // Sends all transactions to the classify endpoint so new ones get categorized.
    // The backend is idempotent — it only inserts rows for transactions with no
    // existing assignment, so calling this after every sync is safe and cheap.
    // Returns the classified map so callers can refresh userAssignments.
    const classifyInBackground = useCallback(async (txns) => {
        if (!txns.length) { setClassifying(false); return; }  // nothing to classify: never leave the spinner on
        setClassifying(true);
        try {
            const res = await fetch(`${API_URL}/api/bank/classify`, {
                method: "POST",
                headers: authHeaders,
                body: JSON.stringify({
                    transactions: txns.map((t) => ({
                        transaction_id:  t.transaction_id,
                        merchant_name:   t.merchant_name || "",
                        name:            t.name || "",
                        amount:          t.amount,
                        bank_category:   t.bank_category || "",
                        account_type:    t.account_type || "",
                    })),
                }),
            });
            // Refresh the assignment map so the Category column updates immediately
            const assignRes = await fetch(`${API_URL}/api/bank/transactions/categories`, { headers: authHeaders });
            if (assignRes.ok) setUserAssignments(await assignRes.json());
            return res.ok ? (await res.json()).classified || {} : {};
        } catch (err) {
            console.error("Classify error (non-critical):", err);
        } finally {
            setClassifying(false);
        }
    }, [authHeaders]);

    // ── Session / internal sync (no rate limit) ───────────────────────────────
    const handleSync = useCallback(async (days) => {
        setSyncing(true);
        try {
            const res = await fetch(`${API_URL}/api/bank/transactions/sync?days=${days}`, {
                method: "POST",
                headers: authHeaders,
            });
            if (res.ok) {
                const data = await res.json();
                const txns = data.transactions || [];
                setTransactions(txns);
                classifyInBackground(txns);
            } else {
                setClassifying(false);
            }
        } catch (err) {
            console.error("Sync error:", err);
            setClassifying(false);
        } finally {
            setSyncing(false);
        }
    }, [authHeaders, classifyInBackground]);

    // ── Calendar sync (user-initiated, once per day) ──────────────────────────
    const handleCalendarSync = useCallback(async () => {
        if (!calendarStartDate) return;
        setShowCalendarSync(false);
        setSyncing(true);
        try {
            const res = await fetch(
                `${API_URL}/api/bank/transactions/sync?start_date=${calendarStartDate}`,
                { method: "POST", headers: authHeaders },
            );
            if (res.status === 429) {
                await Swal.fire({
                    icon: "info",
                    title: "Daily sync limit reached",
                    html: `<p style="font-size:0.9rem;color:#4b5563">We're a smaller company and can't afford unlimited bulk syncs just yet — thanks for understanding!</p>
                           <p style="font-size:0.9rem;color:#4b5563;margin-top:0.5rem">The good news: your transactions are <strong>always refreshed automatically</strong> when you log in, so you're never missing recent activity.</p>`,
                    confirmButtonColor: "#22c55e",
                    confirmButtonText: "Got it",
                });
                return;
            }
            if (res.ok) {
                const data = await res.json();
                const txns = data.transactions || [];
                setTransactions(txns);
                classifyInBackground(txns);
                setCalendarStartDate("");
            }
        } catch (err) {
            console.error("Calendar sync error:", err);
        } finally {
            setSyncing(false);
        }
    }, [calendarStartDate, authHeaders, classifyInBackground]);

    // ── Connect a bank (Stripe's window), then load and classify its transactions ──
    const openConnect = useCallback(async () => {
        setConnectingStatus("Opening your bank's secure sign-in…");
        try {
            const start = await fetch(`${API_URL}/api/bank/link`, { method: "POST", headers: authHeaders });
            if (!start.ok) {
                const body = await start.json().catch(() => ({}));
                await Swal.fire({ icon: "info", title: "Can't connect right now", text: body.detail || "Please try again in a moment.", confirmButtonColor: "#22c55e" });
                return;
            }
            const { client_secret, session_id } = await start.json();
            const stripe = await loadStripe(STRIPE_PK);
            setConnectingStatus(null);  // Stripe's window takes over
            const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret: client_secret });
            if (result.error) throw new Error(result.error.message);
            if (!result.financialConnectionsSession?.accounts?.length) return;  // closed without choosing

            setConnectingStatus("Saving your connection…");
            await fetch(`${API_URL}/api/bank/link/complete`, {
                method: "POST", headers: authHeaders, body: JSON.stringify({ session_id }),
            });
            // Stripe pulls the first transactions in the background, usually within a minute.
            let txns = [];
            for (let attempt = 0; attempt < 12; attempt++) {
                setConnectingStatus(attempt === 0 ? "Loading your transactions…" : "Waiting for your bank's transactions…");
                const syncRes = await fetch(`${API_URL}/api/bank/transactions/sync?days=30`, { method: "POST", headers: authHeaders });
                if (!syncRes.ok) break;  // e.g. no active account to read from
                txns = (await syncRes.json()).transactions || [];
                setTransactions(txns);
                if (txns.length > 0) break;
                await new Promise((r) => setTimeout(r, 5000));
            }
            if (txns.length > 0) {
                setConnectingStatus("Analyzing your transactions…");
                await classifyInBackground(txns);
            }
            setConnectingStatus("Almost done…");
            await refreshAll();
        } catch (err) {
            console.error("Bank connect error:", err);
        } finally {
            setConnectingStatus(null);
        }
    }, [authHeaders, refreshAll, classifyInBackground]);

    // ── Bank link disclaimer ───────────────────────────────────────────────────
    // Shows once per browser session. After the user acknowledges, the connect window opens
    // directly on subsequent clicks.
    const handleOpenConnect = useCallback(() => {
        if (sessionStorage.getItem("bank_disclaimer_accepted")) {
            openConnect();
        } else {
            setShowDisclaimer(true);
        }
    }, [openConnect]);

    const handleDisclaimerAccept = useCallback(() => {
        sessionStorage.setItem("bank_disclaimer_accepted", "1");
        setShowDisclaimer(false);
        openConnect();
    }, [openConnect]);

    // A disconnected account is reconnected by connecting the bank again.
    const openRepairConnect = useCallback(() => openConnect(), [openConnect]);

    // ── Unlink a specific enrollment ──────────────────────────────────────────
    const handleUnlink = useCallback(async (enrollmentId, institutionName) => {
        const confirmed = window.confirm(
            `Unlink ${institutionName || "this account"}? Your transaction history will be removed.`
        );
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_URL}/api/bank/accounts/${enrollmentId}`, {
                method: "DELETE",
                headers: authHeaders,
            });
            if (res.ok) {
                await refreshAll();
            } else {
                alert("Failed to unlink account.");
            }
        } catch (err) {
            console.error("Unlink error:", err);
        }
    }, [authHeaders, refreshAll]);

    // ── Derived ───────────────────────────────────────────────────────────────
    const accountsForFilter = useMemo(() => {
        const seen = new Map();
        balances.forEach(acc => seen.set(acc.account_id, acc.name));
        return Array.from(seen.entries()).map(([id, name]) => ({ account_id: id, name }));
    }, [balances]);

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="flex h-screen bg-gray-100">
                <Sidebar handleSignOut={handleSignOut}>
                    <div className="flex flex-1 items-center justify-center">
                        <div className="flex flex-col items-center gap-3">
                            <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <p className="text-gray-400 text-sm">Loading your accounts…</p>
                        </div>
                    </div>
                </Sidebar>
            </div>
        );
    }

    const anyLinked       = enrollments.length > 0;
    const anyDisconnected = enrollments.some(e => e.disconnected);

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Data usage disclaimer — shown once before the bank connect window opens */}
            {showDisclaimer && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-1">Before you connect your bank</h2>
                        <p className="text-xs text-gray-400 mb-4">Please review how Payclear handles your financial data.</p>

                        <ul className="space-y-3 mb-5">
                            {[
                                {
                                    icon: "🔐",
                                    title: "You sign in to your bank, not to Payclear",
                                    body: "Your bank login happens in a secure window from Stripe. Payclear never sees your bank password and stores no bank credentials.",
                                },
                                {
                                    icon: "📋",
                                    title: "Transactions cached for up to 2 years",
                                    body: "Transaction data (merchant names, amounts, dates) is cached securely to power features. Data is retained for up to 2 years from when it was first synced, and permanently deleted when you disconnect your account.",
                                },
                                {
                                    icon: "🗑️",
                                    title: "Full deletion when you unlink",
                                    body: "Unlinking disconnects the bank and immediately deletes its cached transactions, categories, and notes from Payclear.",
                                },
                                {
                                    icon: "🔗",
                                    title: "Part of your Olene account",
                                    body: "Connected banks belong to your Olene account, so other Olene apps you use show the same banks without connecting again.",
                                },
                                {
                                    icon: "🚫",
                                    title: "Your data is never sold or shared",
                                    body: "Transaction data is used solely to provide features within Payclear. We do not sell, license, or share it with third parties.",
                                },
                            ].map(({ icon, title, body }) => (
                                <li key={title} className="flex gap-3 text-sm">
                                    <span className="text-xl leading-none mt-0.5">{icon}</span>
                                    <div>
                                        <p className="font-medium text-gray-700">{title}</p>
                                        <p className="text-gray-400 text-xs mt-0.5">{body}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDisclaimer(false)}
                                className="flex-1 border border-gray-200 text-gray-500 text-sm py-2 rounded-lg hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDisclaimerAccept}
                                className="flex-1 bg-blue-500 text-white text-sm py-2 rounded-lg hover:bg-blue-600 transition font-medium"
                            >
                                I understand, continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Calendar sync modal */}
            {showCalendarSync && (() => {
                const twoYearsAgo = new Date();
                twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
                const minDate = twoYearsAgo.toLocaleDateString("en-CA");
                const maxDate = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
                return (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
                            <h3 className="text-base font-semibold text-gray-800 mb-1">Pull Historical Transactions</h3>
                            <p className="text-xs text-gray-400 mb-1">Choose how far back to sync. Transactions are always refreshed automatically when you log in.</p>
                            <p className="text-xs text-gray-400 mb-4">This option is limited to once per day.</p>
                            <div className="mb-5">
                                <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
                                <input
                                    type="date"
                                    value={calendarStartDate}
                                    onChange={(e) => setCalendarStartDate(e.target.value)}
                                    min={minDate}
                                    max={maxDate}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowCalendarSync(false); setCalendarStartDate(""); }}
                                    className="flex-1 border border-gray-200 text-gray-500 text-sm py-2 rounded-lg hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCalendarSync}
                                    disabled={!calendarStartDate || syncing}
                                    className="flex-1 bg-green-500 text-white text-sm py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 transition font-medium"
                                >
                                    Sync
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Full-screen overlay during enrollment + initial classification */}
            {connectingStatus && (
                <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                    <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="text-gray-700 font-medium text-sm">{connectingStatus}</p>
                    <p className="text-gray-400 text-xs">This may take a few seconds</p>
                </div>
            )}
            <Sidebar handleSignOut={handleSignOut}>
                <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">

                    {/* Header */}
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Manage Linked Accounts</h1>
                            <p className="text-xs sm:text-sm text-gray-400">
                                {anyLinked
                                    ? `${enrollments.length} institution${enrollments.length !== 1 ? "s" : ""} linked`
                                    : "No accounts linked yet"}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Calendar sync button */}
                            {anyLinked && (
                                <button
                                    onClick={() => setShowCalendarSync(true)}
                                    disabled={syncing}
                                    title="Pull historical transactions"
                                    className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 text-sm font-medium flex items-center gap-1.5"
                                >
                                    {syncing ? (
                                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    )}
                                    {syncing ? "Syncing…" : "Sync"}
                                </button>
                            )}
                            <button
                                onClick={handleOpenConnect}
                                disabled={!connectReady}
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50 text-sm font-medium whitespace-nowrap"
                            >
                                {anyLinked ? "+ Link Another Bank" : "Link Bank Account"}
                            </button>
                        </div>
                    </div>

                    {/* Empty state */}
                    {!anyLinked && (
                        <div className="bg-white shadow-sm rounded-lg p-10 flex flex-col items-center justify-center text-center">
                            <div className="text-5xl mb-4">🏦</div>
                            <h2 className="text-lg font-semibold text-gray-700 mb-2">Connect your bank account</h2>
                            <p className="text-sm text-gray-400 mb-6 max-w-sm">
                                Link your bank to view balances, track spending, and categorize transactions — all in one place.
                            </p>
                            <button
                                onClick={handleOpenConnect}
                                disabled={!connectReady}
                                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition disabled:opacity-50 font-medium"
                            >
                                Link Bank Account
                            </button>
                        </div>
                    )}

                    {/* Enrollment cards */}
                    {anyLinked && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {enrollments.map((enr) => {
                                    const enrAccounts = balances.filter(a => a.enrollment_id === enr.enrollment_id);
                                    return (
                                        <div key={enr.enrollment_id} className="bg-white shadow-sm rounded-lg p-4">
                                            {/* Card header */}
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-base font-semibold text-gray-800">
                                                        {enr.institution_name || "Unknown Institution"}
                                                    </span>
                                                </div>
                                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                                    enr.disconnected
                                                        ? "bg-yellow-100 text-yellow-700"
                                                        : "bg-green-100 text-green-700"
                                                }`}>
                                                    {enr.disconnected ? "Disconnected" : "Connected"}
                                                </span>
                                            </div>

                                            {/* Disconnected notice */}
                                            {enr.disconnected && (
                                                <p className="text-xs text-yellow-700 bg-yellow-50 rounded px-3 py-2 mb-3">
                                                    Your bank stopped sharing this account. Reconnect it to keep transactions up to date, or unlink it.
                                                </p>
                                            )}

                                            {/* Account balances */}
                                            {!enr.disconnected && enrAccounts.length > 0 && (
                                                <div className="space-y-2 mb-4">
                                                    {enrAccounts.map((acc) => (
                                                        <div key={acc.account_id} className="flex items-center justify-between text-sm">
                                                            <div className="flex items-center gap-1 text-gray-600 min-w-0">
                                                                <span className="truncate">{acc.name}</span>
                                                                {acc.last_four && (
                                                                    <span className="text-gray-400 flex-shrink-0">••••{acc.last_four}</span>
                                                                )}
                                                                {acc.subtype && (
                                                                    <span className="text-gray-400 flex-shrink-0 hidden sm:inline">
                                                                        · {acc.subtype.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-right flex-shrink-0 ml-3">
                                                                <span className="font-semibold text-gray-800">
                                                                    {acc.balances.available != null
                                                                        ? `$${acc.balances.available.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                                                                        : acc.balances.ledger != null
                                                                            ? `$${acc.balances.ledger.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                                                                            : "—"}
                                                                </span>
                                                                <span className="text-xs text-gray-400 ml-1">
                                                                    {acc.balances.available != null ? "avail." : "ledger"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {!enr.disconnected && enrAccounts.length === 0 && (
                                                <p className="text-xs text-gray-400 mb-4">Loading accounts...</p>
                                            )}

                                            {/* Actions */}
                                            <div className="flex gap-2 pt-3 border-t border-gray-100">
                                                {enr.disconnected && (
                                                    <button
                                                        onClick={() => openRepairConnect(enr.enrollment_id)}
                                                        className="flex-1 bg-yellow-500 text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-yellow-600 transition"
                                                    >
                                                        Reconnect
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleUnlink(enr.enrollment_id, enr.institution_name)}
                                                    className="flex-1 bg-white border border-red-200 text-red-500 text-xs font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition"
                                                >
                                                    Unlink
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Transactions section */}
                            {!anyDisconnected || enrollments.some(e => !e.disconnected) ? (
                                <div className="bg-white shadow-sm rounded-lg p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                        <p className="text-sm font-semibold text-gray-700">Recent Transactions</p>

                                        {/* Account filter */}
                                        {accountsForFilter.length > 1 && (
                                            <div className="flex gap-2 flex-wrap">
                                                <button
                                                    onClick={() => setSelectedAccount("all")}
                                                    className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                                                        selectedAccount === "all"
                                                            ? "bg-blue-500 text-white border-blue-500"
                                                            : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                                                    }`}
                                                >
                                                    All Accounts
                                                </button>
                                                {accountsForFilter.map((acc) => (
                                                    <button
                                                        key={acc.account_id}
                                                        onClick={() => setSelectedAccount(acc.account_id)}
                                                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                                                            selectedAccount === acc.account_id
                                                                ? "bg-blue-500 text-white border-blue-500"
                                                                : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                                                        }`}
                                                    >
                                                        {acc.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Week navigation */}
                                    <div className="flex items-center justify-between mb-4">
                                        <button
                                            onClick={() => setWeekOffset(w => w - 1)}
                                            disabled={isEarliestAllowedWeek}
                                            className={`px-3 py-1.5 rounded-lg text-sm transition ${
                                                isEarliestAllowedWeek
                                                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            }`}
                                        >
                                            ← Prev
                                        </button>
                                        <span className="text-sm font-medium text-gray-600">
                                            {startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                            {" – "}
                                            {endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                        </span>
                                        <button
                                            onClick={() => setWeekOffset(w => w + 1)}
                                            disabled={isCurrentWeek}
                                            className={`px-3 py-1.5 rounded-lg text-sm transition ${
                                                isCurrentWeek
                                                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            }`}
                                        >
                                            Next →
                                        </button>
                                    </div>

                                    {/* Desktop table */}
                                    <div className="hidden sm:block overflow-x-auto">
                                        <table className="w-full text-sm border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                                                    <th className="px-4 py-2 text-left border border-gray-200">Name</th>
                                                    <th className="px-4 py-2 text-left border border-gray-200">Amount</th>
                                                    <th className="px-4 py-2 text-left border border-gray-200">Date</th>
                                                    <th className="px-4 py-2 text-left border border-gray-200">Status</th>
                                                    <th className="px-4 py-2 text-left border border-gray-200">Category</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {classifying ? (
                                                    <tr>
                                                        <td colSpan={5} className="px-4 py-14 text-center">
                                                            <div className="flex flex-col items-center gap-3">
                                                                <svg className="animate-spin h-8 w-8 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                                </svg>
                                                                <p className="text-sm font-medium text-purple-600">AI is categorizing your transactions…</p>
                                                                <p className="text-xs text-gray-400">This usually takes a few seconds. Your table will appear when it's ready.</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : visibleTransactions.length > 0 ? (
                                                    visibleTransactions.map((txn) => {
                                                        const assignment = userAssignments[txn.transaction_id];
                                                        return (
                                                            <tr key={txn.transaction_id} className="hover:bg-gray-50">
                                                                <td className="px-4 py-2 border border-gray-200 font-medium text-gray-800 truncate max-w-[200px]">
                                                                    {txn.merchant_name || txn.name}
                                                                </td>
                                                                <td className={`px-4 py-2 border border-gray-200 font-semibold ${
                                                                    txn.amount < 0 ? "text-green-600" : "text-red-500"
                                                                }`}>
                                                                    {txn.amount < 0 ? "+" : "-"}${Math.abs(txn.amount).toFixed(2)}
                                                                </td>
                                                                <td className="px-4 py-2 border border-gray-200 text-gray-400">{txn.date}</td>
                                                                <td className="px-4 py-2 border border-gray-200">
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                                        txn.status === "posted"
                                                                            ? "bg-green-100 text-green-700"
                                                                            : "bg-yellow-100 text-yellow-700"
                                                                    }`}>
                                                                        {txn.status ? txn.status[0].toUpperCase() + txn.status.slice(1) : ""}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-2 border border-gray-200">
                                                                    {assignment ? (
                                                                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">
                                                                            {assignment.category_name}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-xs text-gray-300">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={5} className="px-4 py-8 text-center text-gray-400 border border-gray-200">
                                                            No transactions this week
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile cards */}
                                    <div className="sm:hidden space-y-2">
                                        {classifying ? (
                                            <div className="flex flex-col items-center gap-3 py-14">
                                                <svg className="animate-spin h-8 w-8 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                </svg>
                                                <p className="text-sm font-medium text-purple-600">AI is categorizing your transactions…</p>
                                                <p className="text-xs text-gray-400 text-center">This usually takes a few seconds. Your table will appear when it's ready.</p>
                                            </div>
                                        ) : visibleTransactions.length > 0 ? (
                                            visibleTransactions.map((txn) => {
                                                const assignment = userAssignments[txn.transaction_id];
                                                return (
                                                    <div key={txn.transaction_id} className="bg-gray-50 rounded-lg px-3 py-2">
                                                        <div className="flex items-center justify-between">
                                                            <div className="min-w-0 mr-3">
                                                                <p className="text-sm font-medium text-gray-700 truncate">
                                                                    {txn.merchant_name || txn.name}
                                                                </p>
                                                                <p className="text-xs text-gray-400">{txn.date}</p>
                                                            </div>
                                                            <p className={`text-sm font-bold flex-shrink-0 ${
                                                                txn.amount < 0 ? "text-green-600" : "text-red-500"
                                                            }`}>
                                                                {txn.amount < 0 ? "+" : "-"}${Math.abs(txn.amount).toFixed(2)}
                                                            </p>
                                                        </div>
                                                        {assignment && (
                                                            <div className="mt-1.5">
                                                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">
                                                                    {assignment.category_name}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p className="text-center text-gray-400 text-sm py-6">No transactions this week</p>
                                        )}
                                    </div>
                                </div>
                            ) : null}

                        </>
                    )}

                </div>
            </Sidebar>
        </div>
    );
}

export default BankAccount;
